import React, { useState } from 'react'
import { router, usePage, useForm } from '@inertiajs/react'
import {
  Box, Button, Paper, Typography, Chip, Divider,
  Table, TableBody, TableCell, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, List, ListItem, ListItemText, Alert,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import AddIcon from '@mui/icons-material/Add'
import AppShell from '../../../../components/layout/AppShell'
import PageHeader from '../../../../components/ui/PageHeader'
import StatusBadge from '../../../../components/ui/StatusBadge'

Show.layout = (page) => <AppShell>{page}</AppShell>

const STATUS_LABELS = {
  planned: 'Geplant', invited: 'Eingeladen', held: 'Durchgeführt', minutes_completed: 'Protokoll fertig'
}
const NEXT_STATUS_LABELS = {
  planned: 'Als Eingeladen markieren', invited: 'Als Durchgeführt markieren', held: 'Protokoll fertigstellen'
}
const MAJORITY_LABELS = {
  simple_majority: 'Einfache Mehrheit', qualified_majority: 'Qualifizierte Mehrheit',
  unanimity: 'Einstimmigkeit', double_qualified: 'Doppelt qualifizierte Mehrheit'
}
const VOTING_LABELS = {
  head_count: 'Kopfprinzip', value_based: 'Wertprinzip', unit_based: 'Einheitsprinzip'
}
const RESOLUTION_STATUS_LABELS = {
  accepted: 'Angenommen', rejected: 'Abgelehnt', deferred: 'Vertagt',
  challenged: 'Angefochten', final: 'Bestandskräftig', void: 'Ungültig'
}

function AddAgendaItemDialog({ open, onClose, onSave, assemblyId, property, tenant }) {
  const { data, setData, post, processing, reset } = useForm({ title: '', description: '' })

  const handleSave = (e) => {
    e.preventDefault()
    post(`/m/${tenant.slug}/properties/${property.id}/assemblies/${assemblyId}/agenda_items`, {
      onSuccess: () => { reset(); onClose() }
    })
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Tagesordnungspunkt hinzufügen</DialogTitle>
      <DialogContent>
        <Box component="form" id="agenda-form" onSubmit={handleSave} sx={{ pt: 1 }}>
          <TextField label="Titel" required fullWidth size="small" sx={{ mb: 2 }}
            value={data.title} onChange={(e) => setData('title', e.target.value)}
            slotProps={{ htmlInput: { 'data-testid': 'agenda-title-input' } }} />
          <TextField label="Beschreibung" fullWidth size="small" multiline rows={3}
            value={data.description} onChange={(e) => setData('description', e.target.value)} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button type="submit" form="agenda-form" variant="contained" disabled={processing}
          data-testid="save-agenda-item-btn">
          Hinzufügen
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default function Show({ property, assembly, agenda_items }) {
  const { props } = usePage()
  const tenant = props.tenant || {}
  const [agendaDialogOpen, setAgendaDialogOpen] = useState(false)

  const canAdvanceStatus = ['planned', 'invited', 'held'].includes(assembly.status)

  const handleAdvanceStatus = () => {
    router.patch(`/m/${tenant.slug}/properties/${property.id}/assemblies/${assembly.id}/advance_status`)
  }

  return (
    <Box>
      <PageHeader
        title={`Versammlung ${assembly.date}`}
        breadcrumbs={[
          { label: 'Versammlungen', href: `/m/${tenant.slug}/properties/${property.id}/assemblies` },
          { label: assembly.date },
        ]}
        actions={
          <Box sx={{ display: 'flex', gap: 1 }}>
            {canAdvanceStatus && (
              <Button variant="outlined" size="small" onClick={handleAdvanceStatus} data-testid="advance-status-btn">
                {NEXT_STATUS_LABELS[assembly.status]}
              </Button>
            )}
            <Button variant="outlined" startIcon={<EditIcon />}
              onClick={() => router.visit(`/m/${tenant.slug}/properties/${property.id}/assemblies/${assembly.id}/edit`)}>
              Bearbeiten
            </Button>
          </Box>
        }
      />

      {/* Assembly header */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <StatusBadge type="assembly_type" value={assembly.assembly_type} />
          <StatusBadge type="assembly_status" value={assembly.status} />
          <Typography variant="body2" color="text.secondary">
            {assembly.date}{assembly.time ? `, ${assembly.time} Uhr` : ''}
          </Typography>
          {assembly.location && <Typography variant="body2" color="text.secondary">{assembly.location}</Typography>}
          {assembly.chairperson && <Typography variant="body2" color="text.secondary">Leiter: {assembly.chairperson}</Typography>}
          {assembly.minutes_taker && <Typography variant="body2" color="text.secondary">Protokoll: {assembly.minutes_taker}</Typography>}
          {assembly.attendee_count != null && (
            <Typography variant="body2" color="text.secondary">
              Anwesend: {assembly.attendee_count} {assembly.quorate != null && `· ${assembly.quorate ? 'Beschlussfähig' : 'Nicht beschlussfähig'}`}
            </Typography>
          )}
        </Box>
      </Paper>

      {/* Tagesordnung */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6" fontWeight={600}>Tagesordnung</Typography>
        <Button size="small" startIcon={<AddIcon />}
          onClick={() => setAgendaDialogOpen(true)} data-testid="add-agenda-item-btn">
          TOP hinzufügen
        </Button>
      </Box>

      {agenda_items.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Noch keine Tagesordnungspunkte. Fügen Sie TOPs hinzu, um Beschlüsse zu erfassen.
        </Alert>
      )}

      {agenda_items.map((item) => (
        <Paper key={item.id} elevation={0}
          sx={{ p: 2, mb: 1, border: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="body1" fontWeight={500}>
                TOP {item.position}: {item.title}
              </Typography>
              {item.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {item.description}
                </Typography>
              )}
            </Box>
            <Button size="small" startIcon={<AddIcon />}
              onClick={() => router.visit(
                `/m/${tenant.slug}/properties/${property.id}/assemblies/${assembly.id}/resolutions/new?agenda_item_id=${item.id}`
              )}
              data-testid={`add-resolution-${item.id}`}
            >
              Beschluss
            </Button>
          </Box>

          {item.resolutions.length > 0 && (
            <Box sx={{ mt: 1, ml: 2 }}>
              {item.resolutions.map((resolution) => (
                <Box key={resolution.id} sx={{ display: 'flex', gap: 2, alignItems: 'center', py: 0.5,
                  cursor: 'pointer', '&:hover': { bgcolor: 'background.subtle' }, borderRadius: 1, px: 1 }}
                  onClick={() => router.visit(`/m/${tenant.slug}/properties/${property.id}/resolutions/${resolution.id}`)}>
                  <Typography variant="body2" color="text.secondary">
                    #{resolution.resolution_number}
                  </Typography>
                  <Typography variant="body2" sx={{ flex: 1 }}>{resolution.title}</Typography>
                  <StatusBadge type="resolution_status" value={resolution.status} size="small" />
                  {resolution.votes_for != null && (
                    <Typography variant="caption" color="text.secondary">
                      Ja: {resolution.votes_for} / Nein: {resolution.votes_against} / Enth.: {resolution.abstentions}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          )}

          {item.resolutions.length === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 2, display: 'block', mt: 0.5 }}>
              (kein Beschluss)
            </Typography>
          )}
        </Paper>
      ))}

      <AddAgendaItemDialog
        open={agendaDialogOpen}
        onClose={() => setAgendaDialogOpen(false)}
        assemblyId={assembly.id}
        property={property}
        tenant={tenant}
      />
    </Box>
  )
}
